import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../../api/client.js';
import type {
  AdminFacility,
  AdminProgram,
  AdminTaskOrder,
  AdminUser,
  Organization,
} from '../../api/types.js';
import { useSession } from '../../auth/SessionProvider.js';
import { PERMISSIONS } from '../../../shared/permissions.js';
import {
  Alert,
  Button,
  Card,
  DefinitionList,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../../components/ui.js';
import { formatDate } from '../../lib/format.js';

const TIME_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
] as const;

export function AdminTaskOrdersTab() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [panel, setPanel] = useState<'none' | 'program' | 'taskOrder'>('none');
  const [configuring, setConfiguring] = useState<AdminTaskOrder | null>(null);
  const [error, setError] = useState<string | null>(null);

  const programs = useQuery({
    queryKey: ['admin', 'programs'],
    queryFn: () =>
      apiRequest<{ programs: AdminProgram[] }>('/api/admin/programs').then(
        (result) => result.programs,
      ),
  });
  const taskOrders = useQuery({
    queryKey: ['admin', 'task-orders'],
    queryFn: () =>
      apiRequest<{ taskOrders: AdminTaskOrder[] }>('/api/admin/task-orders').then(
        (result) => result.taskOrders,
      ),
  });
  const facilities = useQuery({
    queryKey: ['admin', 'facilities'],
    queryFn: () =>
      apiRequest<{ facilities: AdminFacility[] }>('/api/admin/facilities').then(
        (result) => result.facilities,
      ),
    enabled: can(PERMISSIONS.ADMIN_FACILITY_MANAGE),
  });
  const organizations = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: () =>
      apiRequest<{ organizations: Organization[] }>('/api/admin/organizations').then(
        (result) => result.organizations,
      ),
  });

  const [programForm, setProgramForm] = useState({
    code: '',
    name: '',
    customerOrganizationId: '',
    description: '',
  });

  const [taskOrderForm, setTaskOrderForm] = useState({
    programId: '',
    number: '',
    name: '',
    contractNumber: '',
    popStartDate: '',
    popEndDate: '',
    markupPercent: '9',
    responseTargetEmergencyHours: '24',
    responseTargetUrgentHours: '72',
    responseTargetRoutineHours: '240',
    responseTargetScheduledHours: '720',
    facilityCode: '',
    facilityName: '',
    addressLine1: '',
    city: '',
    state: '',
    postalCode: '',
    timezone: 'America/Chicago',
    propertyId: '',
    uspsArea: '',
    uspsDistrict: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
  });

  const [configForm, setConfigForm] = useState({
    markupPercent: '',
    responseTargetEmergencyHours: '24',
    responseTargetUrgentHours: '72',
    responseTargetRoutineHours: '240',
    responseTargetScheduledHours: '720',
    changeReason: '',
  });

  const onError = (caught: unknown) => {
    setError(caught instanceof ApiRequestError ? caught.message : 'The change could not be saved.');
  };

  const createProgram = useMutation({
    mutationFn: () =>
      apiRequest('/api/admin/programs', {
        method: 'POST',
        body: {
          code: programForm.code.trim().toUpperCase(),
          name: programForm.name.trim(),
          customerOrganizationId: programForm.customerOrganizationId,
          description: programForm.description.trim() || undefined,
        },
      }),
    onSuccess: () => {
      setPanel('none');
      setProgramForm({ code: '', name: '', customerOrganizationId: '', description: '' });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'programs'] });
    },
    onError,
  });

  const createTaskOrder = useMutation({
    mutationFn: () =>
      apiRequest('/api/admin/task-orders', {
        method: 'POST',
        body: {
          programId: taskOrderForm.programId,
          number: taskOrderForm.number.trim(),
          name: taskOrderForm.name.trim(),
          contractNumber: taskOrderForm.contractNumber.trim() || undefined,
          popStartDate: taskOrderForm.popStartDate || undefined,
          popEndDate: taskOrderForm.popEndDate || undefined,
          markupPercent: taskOrderForm.markupPercent.trim(),
          responseTargetEmergencyHours: Number(taskOrderForm.responseTargetEmergencyHours),
          responseTargetUrgentHours: Number(taskOrderForm.responseTargetUrgentHours),
          responseTargetRoutineHours: Number(taskOrderForm.responseTargetRoutineHours),
          responseTargetScheduledHours: Number(taskOrderForm.responseTargetScheduledHours),
          facility: {
            facilityCode: taskOrderForm.facilityCode.trim().toUpperCase(),
            name: taskOrderForm.facilityName.trim(),
            addressLine1: taskOrderForm.addressLine1.trim() || undefined,
            city: taskOrderForm.city.trim() || undefined,
            state: taskOrderForm.state.trim().toUpperCase() || undefined,
            postalCode: taskOrderForm.postalCode.trim() || undefined,
            timezone: taskOrderForm.timezone,
            propertyId: taskOrderForm.propertyId.trim() || undefined,
            uspsArea: taskOrderForm.uspsArea.trim() || undefined,
            uspsDistrict: taskOrderForm.uspsDistrict.trim() || undefined,
            contactName: taskOrderForm.contactName.trim() || undefined,
            contactPhone: taskOrderForm.contactPhone.trim() || undefined,
            contactEmail: taskOrderForm.contactEmail.trim() || undefined,
          },
        },
      }),
    onSuccess: () => {
      setPanel('none');
      setTaskOrderForm({
        ...taskOrderForm,
        number: '',
        name: '',
        contractNumber: '',
        facilityCode: '',
        facilityName: '',
        addressLine1: '',
        city: '',
        state: '',
        postalCode: '',
        propertyId: '',
        uspsArea: '',
        uspsDistrict: '',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
      });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'task-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'facilities'] });
      void queryClient.invalidateQueries({ queryKey: ['reference', 'facilities'] });
    },
    onError,
  });

  const saveConfiguration = useMutation({
    mutationFn: (taskOrderId: string) =>
      apiRequest(`/api/admin/task-orders/${taskOrderId}/configuration`, {
        method: 'PUT',
        body: {
          markupPercent: configForm.markupPercent.trim(),
          responseTargetEmergencyHours: Number(configForm.responseTargetEmergencyHours),
          responseTargetUrgentHours: Number(configForm.responseTargetUrgentHours),
          responseTargetRoutineHours: Number(configForm.responseTargetRoutineHours),
          responseTargetScheduledHours: Number(configForm.responseTargetScheduledHours),
          changeReason: configForm.changeReason.trim(),
        },
      }),
    onSuccess: () => {
      setConfiguring(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'task-orders'] });
    },
    onError,
  });

  const customerOrganizations = (organizations.data ?? []).filter(
    (organization) => organization.orgType === 'customer',
  );

  return (
    <>
      <PageHeader
        title="Task orders"
        subtitle="Each task order covers a facility and carries the markup rate and response targets applied to its work. The facility code becomes part of every work order number there, so it cannot change once work orders exist."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => setPanel(panel === 'program' ? 'none' : 'program')}
            >
              Add program
            </Button>
            <Button onClick={() => setPanel(panel === 'taskOrder' ? 'none' : 'taskOrder')}>
              {panel === 'taskOrder' ? 'Cancel' : 'Add task order / facility'}
            </Button>
          </>
        }
      />

      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {panel === 'program' && (
        <Card title="Add a program" className="mb-4">
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="Code"
                htmlFor="programCode"
                required
                hint="Uppercase letters, digits, dashes."
              >
                <TextInput
                  id="programCode"
                  value={programForm.code}
                  onChange={(event) =>
                    setProgramForm({ ...programForm, code: event.target.value.toUpperCase() })
                  }
                />
              </Field>
              <Field label="Name" htmlFor="programName" required>
                <TextInput
                  id="programName"
                  value={programForm.name}
                  onChange={(event) => setProgramForm({ ...programForm, name: event.target.value })}
                />
              </Field>
              <Field label="Customer organization" htmlFor="customerOrganizationId" required>
                <Select
                  id="customerOrganizationId"
                  value={programForm.customerOrganizationId}
                  onChange={(event) =>
                    setProgramForm({ ...programForm, customerOrganizationId: event.target.value })
                  }
                >
                  <option value="">Select an organization…</option>
                  {customerOrganizations.map((organization) => (
                    <option key={organization.id} value={organization.id}>
                      {organization.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label="Description" htmlFor="programDescription">
              <TextArea
                id="programDescription"
                rows={2}
                value={programForm.description}
                onChange={(event) =>
                  setProgramForm({ ...programForm, description: event.target.value })
                }
              />
            </Field>
            <div>
              <Button
                onClick={() => {
                  setError(null);
                  createProgram.mutate();
                }}
                disabled={
                  programForm.code.trim().length < 2 ||
                  programForm.name.trim().length < 2 ||
                  programForm.customerOrganizationId === ''
                }
                loading={createProgram.isPending}
              >
                Add program
              </Button>
            </div>
          </div>
        </Card>
      )}

      {panel === 'taskOrder' && (
        <Card title="Add a task order / facility" className="mb-4">
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Program" htmlFor="programId" required>
                <Select
                  id="programId"
                  value={taskOrderForm.programId}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, programId: event.target.value })
                  }
                >
                  <option value="">Select a program…</option>
                  {(programs.data ?? []).map((program) => (
                    <option key={program.id} value={program.id}>
                      {program.code} — {program.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Task order number" htmlFor="taskOrderNumber" required>
                <TextInput
                  id="taskOrderNumber"
                  value={taskOrderForm.number}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, number: event.target.value })
                  }
                />
              </Field>
              <Field label="Name" htmlFor="taskOrderName" required>
                <TextInput
                  id="taskOrderName"
                  value={taskOrderForm.name}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, name: event.target.value })
                  }
                />
              </Field>
              <Field label="Contract number" htmlFor="contractNumber">
                <TextInput
                  id="contractNumber"
                  value={taskOrderForm.contractNumber}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, contractNumber: event.target.value })
                  }
                />
              </Field>
              <Field label="Period of performance start" htmlFor="popStartDate">
                <TextInput
                  id="popStartDate"
                  type="date"
                  value={taskOrderForm.popStartDate}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, popStartDate: event.target.value })
                  }
                />
              </Field>
              <Field label="Period of performance end" htmlFor="popEndDate">
                <TextInput
                  id="popEndDate"
                  type="date"
                  value={taskOrderForm.popEndDate}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, popEndDate: event.target.value })
                  }
                />
              </Field>
              <Field
                label="Markup percent"
                htmlFor="markupPercent"
                required
                hint="Applied to reimbursable-expense proposals, e.g. 9 for 9%."
              >
                <TextInput
                  id="markupPercent"
                  inputMode="decimal"
                  value={taskOrderForm.markupPercent}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, markupPercent: event.target.value })
                  }
                />
              </Field>
            </div>

            <fieldset className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <legend className="mb-1 text-sm font-semibold text-slate-900">
                Response targets (hours)
              </legend>
              {(
                [
                  ['responseTargetEmergencyHours', 'Emergency'],
                  ['responseTargetUrgentHours', 'Urgent'],
                  ['responseTargetRoutineHours', 'Routine'],
                  ['responseTargetScheduledHours', 'Scheduled'],
                ] as const
              ).map(([field, label]) => (
                <Field key={field} label={label} htmlFor={field}>
                  <TextInput
                    id={field}
                    inputMode="numeric"
                    value={taskOrderForm[field]}
                    onChange={(event) =>
                      setTaskOrderForm({ ...taskOrderForm, [field]: event.target.value })
                    }
                  />
                </Field>
              ))}
            </fieldset>

            <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <legend className="mb-1 text-sm font-semibold text-slate-900">
                Facility this task order covers
              </legend>
              <Field
                label="Facility code"
                htmlFor="facilityCode"
                required
                hint="2–10 uppercase letters or digits, e.g. TUL. Permanent."
              >
                <TextInput
                  id="facilityCode"
                  value={taskOrderForm.facilityCode}
                  onChange={(event) =>
                    setTaskOrderForm({
                      ...taskOrderForm,
                      facilityCode: event.target.value.toUpperCase(),
                    })
                  }
                />
              </Field>
              <Field label="Facility name" htmlFor="facilityName" required>
                <TextInput
                  id="facilityName"
                  value={taskOrderForm.facilityName}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, facilityName: event.target.value })
                  }
                />
              </Field>
              <Field label="Street address" htmlFor="addressLine1">
                <TextInput
                  id="addressLine1"
                  value={taskOrderForm.addressLine1}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, addressLine1: event.target.value })
                  }
                />
              </Field>
              <Field label="City" htmlFor="city">
                <TextInput
                  id="city"
                  value={taskOrderForm.city}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, city: event.target.value })
                  }
                />
              </Field>
              <Field label="State" htmlFor="state" hint="Two-letter abbreviation.">
                <TextInput
                  id="state"
                  maxLength={2}
                  value={taskOrderForm.state}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, state: event.target.value.toUpperCase() })
                  }
                />
              </Field>
              <Field label="ZIP code" htmlFor="postalCode">
                <TextInput
                  id="postalCode"
                  value={taskOrderForm.postalCode}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, postalCode: event.target.value })
                  }
                />
              </Field>
              <Field label="Time zone" htmlFor="timezone">
                <Select
                  id="timezone"
                  value={taskOrderForm.timezone}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, timezone: event.target.value })
                  }
                >
                  {TIME_ZONES.map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Property ID"
                htmlFor="propertyId"
                hint="Postal Service identifier for the site."
              >
                <TextInput
                  id="propertyId"
                  value={taskOrderForm.propertyId}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, propertyId: event.target.value })
                  }
                />
              </Field>
              <Field label="Postal Service Area/Logistics" htmlFor="uspsArea">
                <TextInput
                  id="uspsArea"
                  value={taskOrderForm.uspsArea}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, uspsArea: event.target.value })
                  }
                />
              </Field>
              <Field label="Postal Service District/Division" htmlFor="uspsDistrict">
                <TextInput
                  id="uspsDistrict"
                  value={taskOrderForm.uspsDistrict}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, uspsDistrict: event.target.value })
                  }
                />
              </Field>
              <Field
                label="Local facility contact"
                htmlFor="contactName"
                hint="Reported to USPS with each work order."
              >
                <TextInput
                  id="contactName"
                  value={taskOrderForm.contactName}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, contactName: event.target.value })
                  }
                />
              </Field>
              <Field label="Contact phone" htmlFor="contactPhone">
                <TextInput
                  id="contactPhone"
                  value={taskOrderForm.contactPhone}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, contactPhone: event.target.value })
                  }
                />
              </Field>
              <Field label="Contact email" htmlFor="contactEmail">
                <TextInput
                  id="contactEmail"
                  type="email"
                  value={taskOrderForm.contactEmail}
                  onChange={(event) =>
                    setTaskOrderForm({ ...taskOrderForm, contactEmail: event.target.value })
                  }
                />
              </Field>
            </fieldset>

            <div>
              <Button
                onClick={() => {
                  setError(null);
                  createTaskOrder.mutate();
                }}
                disabled={
                  taskOrderForm.programId === '' ||
                  taskOrderForm.number.trim().length < 2 ||
                  taskOrderForm.name.trim().length < 2 ||
                  !/^\d{1,2}(\.\d{1,2})?$/.test(taskOrderForm.markupPercent.trim()) ||
                  !/^[A-Z0-9]{2,10}$/.test(taskOrderForm.facilityCode.trim()) ||
                  taskOrderForm.facilityName.trim().length < 2
                }
                loading={createTaskOrder.isPending}
              >
                Add task order and facility
              </Button>
            </div>
          </div>
        </Card>
      )}

      {configuring && (
        <Card title={`Change configuration for ${configuring.number}`} className="mb-4">
          <div className="flex flex-col gap-4">
            <Alert variant="info">
              A change creates a new configuration version. Proposals already prepared keep the
              markup rate that was in effect when they were written.
            </Alert>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Markup percent" htmlFor="configMarkup" required>
                <TextInput
                  id="configMarkup"
                  inputMode="decimal"
                  value={configForm.markupPercent}
                  onChange={(event) =>
                    setConfigForm({ ...configForm, markupPercent: event.target.value })
                  }
                />
              </Field>
              <Field label="Reason for the change" htmlFor="changeReason" required>
                <TextInput
                  id="changeReason"
                  value={configForm.changeReason}
                  onChange={(event) =>
                    setConfigForm({ ...configForm, changeReason: event.target.value })
                  }
                />
              </Field>
            </div>
            <fieldset className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <legend className="mb-1 text-sm font-semibold text-slate-900">
                Response targets (hours)
              </legend>
              {(
                [
                  ['responseTargetEmergencyHours', 'Emergency'],
                  ['responseTargetUrgentHours', 'Urgent'],
                  ['responseTargetRoutineHours', 'Routine'],
                  ['responseTargetScheduledHours', 'Scheduled'],
                ] as const
              ).map(([field, label]) => (
                <Field key={field} label={label} htmlFor={`config-${field}`}>
                  <TextInput
                    id={`config-${field}`}
                    inputMode="numeric"
                    value={configForm[field]}
                    onChange={(event) =>
                      setConfigForm({ ...configForm, [field]: event.target.value })
                    }
                  />
                </Field>
              ))}
            </fieldset>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setError(null);
                  saveConfiguration.mutate(configuring.id);
                }}
                disabled={
                  !/^\d{1,2}(\.\d{1,2})?$/.test(configForm.markupPercent.trim()) ||
                  configForm.changeReason.trim().length < 3
                }
                loading={saveConfiguration.isPending}
              >
                Save configuration
              </Button>
              <Button variant="secondary" onClick={() => setConfiguring(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {taskOrders.isLoading ? (
        <Spinner label="Loading task orders" />
      ) : (taskOrders.data?.length ?? 0) === 0 ? (
        <Card>
          <EmptyState
            title="No task orders yet"
            description="Add a program, then the task order it covers."
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {taskOrders.data?.map((taskOrder) => (
            <Card
              key={taskOrder.id}
              title={`${taskOrder.number} — ${taskOrder.name}`}
              actions={
                can(PERMISSIONS.ADMIN_MARKUP_CONFIGURE) && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setConfiguring(taskOrder);
                      setConfigForm({
                        markupPercent: taskOrder.markupPercentDisplay ?? '9.00',
                        // The whole configuration row is replaced on save, so the
                        // form must start from the task order's current targets.
                        responseTargetEmergencyHours: String(
                          taskOrder.responseTargetEmergencyHours ?? 24,
                        ),
                        responseTargetUrgentHours: String(
                          taskOrder.responseTargetUrgentHours ?? 72,
                        ),
                        responseTargetRoutineHours: String(
                          taskOrder.responseTargetRoutineHours ?? 240,
                        ),
                        responseTargetScheduledHours: String(
                          taskOrder.responseTargetScheduledHours ?? 720,
                        ),
                        changeReason: '',
                      });
                    }}
                  >
                    Change configuration
                  </Button>
                )
              }
            >
              <DefinitionList
                columns={3}
                items={[
                  { label: 'Program', value: taskOrder.programName },
                  { label: 'Contract', value: taskOrder.contractNumber },
                  {
                    label: 'Acting COR',
                    value: <ActingCorPicker taskOrder={taskOrder} />,
                  },
                  {
                    label: 'Markup',
                    value: taskOrder.markupPercentDisplay
                      ? `${taskOrder.markupPercentDisplay}%`
                      : null,
                  },
                  {
                    label: 'Period of performance',
                    value:
                      taskOrder.popStartDate || taskOrder.popEndDate
                        ? `${formatDate(taskOrder.popStartDate)} – ${formatDate(taskOrder.popEndDate)}`
                        : null,
                  },
                  {
                    label: 'Status',
                    value: taskOrder.closedAt
                      ? `Closed ${formatDate(taskOrder.closedAt)}`
                      : taskOrder.isActive
                        ? 'Active'
                        : 'Inactive',
                  },
                  {
                    label: 'Records retained until',
                    value: taskOrder.retentionUntil
                      ? formatDate(taskOrder.retentionUntil)
                      : 'Set six years after closure',
                  },
                  { label: 'Legal hold', value: taskOrder.legalHold ? 'Yes' : 'No' },
                ]}
              />

              {can(PERMISSIONS.ADMIN_FACILITY_MANAGE) && (
                <FacilitiesForTaskOrder
                  taskOrderId={taskOrder.id}
                  facilities={(facilities.data ?? []).filter(
                    (facility) => facility.taskOrderId === taskOrder.id,
                  )}
                  isLoading={facilities.isLoading}
                  onError={onError}
                  clearError={() => setError(null)}
                />
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * The facility attributes USPS asks for in the work order report but that no other part
 * of the workflow needs, so they are edited here rather than mixed into the facility's
 * own details.
 */
function FacilityReportingFields({
  facility,
  onSaved,
  onError,
  clearError,
}: {
  facility: AdminFacility;
  onSaved: () => void;
  onError: (caught: unknown) => void;
  clearError: () => void;
}) {
  const [form, setForm] = useState({
    propertyId: facility.propertyId ?? '',
    uspsArea: facility.uspsArea ?? '',
    uspsDistrict: facility.uspsDistrict ?? '',
    contactName: facility.contactName ?? '',
    contactPhone: facility.contactPhone ?? '',
    contactEmail: facility.contactEmail ?? '',
  });

  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/admin/facilities/${facility.id}`, {
        method: 'PATCH',
        body: {
          propertyId: form.propertyId.trim() || null,
          uspsArea: form.uspsArea.trim() || null,
          uspsDistrict: form.uspsDistrict.trim() || null,
          contactName: form.contactName.trim() || null,
          contactPhone: form.contactPhone.trim() || null,
          contactEmail: form.contactEmail.trim() || null,
        },
      }),
    onSuccess: onSaved,
    onError,
  });

  const fields: { key: keyof typeof form; label: string; type?: string }[] = [
    { key: 'propertyId', label: 'Property ID' },
    { key: 'uspsArea', label: 'Postal Service Area/Logistics' },
    { key: 'uspsDistrict', label: 'Postal Service District/Division' },
    { key: 'contactName', label: 'Local facility contact' },
    { key: 'contactPhone', label: 'Contact phone' },
    { key: 'contactEmail', label: 'Contact email', type: 'email' },
  ];

  return (
    <div className="mt-3 flex flex-col gap-3 rounded-md bg-slate-50 p-3">
      <p className="text-sm text-slate-600">
        Reported to USPS with every work order at {facility.facilityCode}.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {fields.map((field) => (
          <Field key={field.key} label={field.label} htmlFor={`${field.key}-${facility.id}`}>
            <TextInput
              id={`${field.key}-${facility.id}`}
              type={field.type}
              value={form[field.key]}
              onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
            />
          </Field>
        ))}
      </div>
      <div>
        <Button
          onClick={() => {
            clearError();
            save.mutate();
          }}
          loading={save.isPending}
        >
          Save reporting details
        </Button>
      </div>
    </div>
  );
}

/** Every facility field an administrator may correct in place. */
function FacilityEditFields({
  facility,
  onSaved,
  onCancel,
  onError,
  clearError,
}: {
  facility: AdminFacility;
  onSaved: () => void;
  onCancel: () => void;
  onError: (caught: unknown) => void;
  clearError: () => void;
}) {
  const codeLocked = facility.workOrderCount > 0;
  const [form, setForm] = useState({
    facilityCode: facility.facilityCode,
    name: facility.name,
    addressLine1: facility.addressLine1 ?? '',
    city: facility.city ?? '',
    state: facility.state ?? '',
    postalCode: facility.postalCode ?? '',
    timezone: facility.timezone,
    propertyId: facility.propertyId ?? '',
    uspsArea: facility.uspsArea ?? '',
    uspsDistrict: facility.uspsDistrict ?? '',
    contactName: facility.contactName ?? '',
    contactPhone: facility.contactPhone ?? '',
    contactEmail: facility.contactEmail ?? '',
  });
  const code = form.facilityCode.trim().toUpperCase();
  const codeValid = /^[A-Z0-9]{2,10}$/.test(code);
  const nameValid = form.name.trim().length >= 2;
  const stateValid = form.state.trim() === '' || /^[A-Za-z]{2}$/.test(form.state.trim());

  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/admin/facilities/${facility.id}`, {
        method: 'PATCH',
        body: {
          ...(codeLocked || code === facility.facilityCode ? {} : { facilityCode: code }),
          name: form.name.trim(),
          addressLine1: form.addressLine1.trim() || null,
          city: form.city.trim() || null,
          state: form.state.trim().toUpperCase() || null,
          postalCode: form.postalCode.trim() || null,
          timezone: form.timezone.trim() || 'America/Chicago',
          propertyId: form.propertyId.trim() || null,
          uspsArea: form.uspsArea.trim() || null,
          uspsDistrict: form.uspsDistrict.trim() || null,
          contactName: form.contactName.trim() || null,
          contactPhone: form.contactPhone.trim() || null,
          contactEmail: form.contactEmail.trim() || null,
        },
      }),
    onSuccess: onSaved,
    onError,
  });

  const text = (key: keyof typeof form, label: string, type?: string, error?: string) => (
    <Field key={key} label={label} htmlFor={`edit-${key}-${facility.id}`} error={error}>
      <TextInput
        id={`edit-${key}-${facility.id}`}
        type={type}
        value={form[key]}
        onChange={(event) => setForm({ ...form, [key]: event.target.value })}
      />
    </Field>
  );

  return (
    <div className="mt-3 flex flex-col gap-3 rounded-md bg-slate-50 p-3">
      <p className="text-sm text-slate-600">
        {codeLocked
          ? `The code ${facility.facilityCode} is part of ${facility.workOrderCount} work order number${facility.workOrderCount === 1 ? '' : 's'} and can no longer change; everything else can.`
          : 'The code becomes the prefix of every work order number here, so it can only be changed until the first work order is created.'}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field
          label="Facility code"
          htmlFor={`edit-facilityCode-${facility.id}`}
          required
          hint="2–10 uppercase letters or digits, no spaces or punctuation."
          error={
            !codeValid
              ? 'Use 2–10 uppercase letters or digits only — no spaces, hyphens or other punctuation.'
              : undefined
          }
        >
          <TextInput
            id={`edit-facilityCode-${facility.id}`}
            value={form.facilityCode}
            disabled={codeLocked}
            onChange={(event) =>
              setForm({ ...form, facilityCode: event.target.value.toUpperCase() })
            }
          />
        </Field>
        <Field
          label="Facility name"
          htmlFor={`edit-name-${facility.id}`}
          required
          error={!nameValid ? 'Enter the facility name (at least 2 characters).' : undefined}
        >
          <TextInput
            id={`edit-name-${facility.id}`}
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </Field>
        {text('timezone', 'Time zone (IANA, e.g. America/Chicago)')}
        {text('addressLine1', 'Street address')}
        {text('city', 'City')}
        {text(
          'state',
          'State (2 letters)',
          undefined,
          !stateValid ? 'Use the two-letter state abbreviation.' : undefined,
        )}
        {text('postalCode', 'ZIP code')}
        {text('propertyId', 'Property ID')}
        {text('uspsArea', 'Postal Service Area/Logistics')}
        {text('uspsDistrict', 'Postal Service District/Division')}
        {text('contactName', 'Local facility contact')}
        {text('contactPhone', 'Contact phone')}
        {text('contactEmail', 'Contact email', 'email')}
      </div>
      <div className="flex items-center gap-2">
        <Button
          onClick={() => {
            clearError();
            save.mutate();
          }}
          disabled={!codeValid || !nameValid || !stateValid}
          loading={save.isPending}
        >
          Save facility
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * The facilities a task order covers. Normally exactly one under this agreement, so
 * the pair is created together and shown together; a second is added here rather than
 * from a separate screen, where its task order would have to be chosen again.
 */
function FacilitiesForTaskOrder({
  taskOrderId,
  facilities,
  isLoading,
  onError,
  clearError,
}: {
  taskOrderId: string;
  facilities: AdminFacility[];
  isLoading: boolean;
  onError: (caught: unknown) => void;
  clearError: () => void;
}) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [panel, setPanel] = useState<'edit' | 'reporting'>('reporting');
  const [form, setForm] = useState({
    facilityCode: '',
    name: '',
    city: '',
    state: '',
    timezone: 'America/Chicago',
  });
  const codeValid = /^[A-Z0-9]{2,10}$/.test(form.facilityCode.trim());
  const nameValid = form.name.trim().length >= 2;
  const codeError =
    form.facilityCode.length > 0 && !codeValid
      ? 'Use 2–10 uppercase letters or digits only — no spaces, hyphens or other punctuation.'
      : undefined;
  const nameError =
    form.name.length > 0 && !nameValid
      ? 'Enter the facility name (at least 2 characters).'
      : undefined;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'facilities'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'task-orders'] });
    void queryClient.invalidateQueries({ queryKey: ['reference', 'facilities'] });
  };

  const create = useMutation({
    mutationFn: () =>
      apiRequest('/api/admin/facilities', {
        method: 'POST',
        body: {
          taskOrderId,
          facilityCode: form.facilityCode.trim().toUpperCase(),
          name: form.name.trim(),
          city: form.city.trim() || undefined,
          state: form.state.trim().toUpperCase() || undefined,
          timezone: form.timezone,
        },
      }),
    onSuccess: () => {
      setAdding(false);
      setForm({ facilityCode: '', name: '', city: '', state: '', timezone: 'America/Chicago' });
      invalidate();
    },
    onError,
  });

  const setActive = useMutation({
    mutationFn: ({ facilityId, isActive }: { facilityId: string; isActive: boolean }) =>
      apiRequest(`/api/admin/facilities/${facilityId}`, { method: 'PATCH', body: { isActive } }),
    onSuccess: invalidate,
    onError,
  });

  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-slate-900">
          {isLoading || facilities.length === 1 ? 'Facility' : `Facilities (${facilities.length})`}
        </h4>
        <Button variant="ghost" onClick={() => setAdding((previous) => !previous)}>
          {adding ? 'Cancel' : 'Add another facility'}
        </Button>
      </div>

      {isLoading ? (
        <Spinner label="Loading facilities" />
      ) : facilities.length === 0 ? (
        <p className="text-sm text-slate-600">
          No facility yet, so this task order cannot receive work orders.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <caption className="sr-only">Facilities covered by this task order</caption>
            <thead className="bg-slate-50">
              <tr>
                <th scope="col" className="table-header-cell">
                  Code
                </th>
                <th scope="col" className="table-header-cell">
                  Name
                </th>
                <th scope="col" className="table-header-cell">
                  Location
                </th>
                <th scope="col" className="table-header-cell text-right">
                  Work orders
                </th>
                <th scope="col" className="table-header-cell text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {facilities.map((facility) => (
                <tr key={facility.id} className={facility.isActive ? undefined : 'bg-slate-50'}>
                  <td className="table-cell-base font-mono text-xs font-semibold">
                    {facility.facilityCode}
                  </td>
                  <td className="table-cell-base">
                    {facility.name}
                    {!facility.isActive && (
                      <span className="ml-1 rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-700">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="table-cell-base">
                    {[facility.city, facility.state].filter(Boolean).join(', ') || '—'}
                    <span className="block text-xs text-slate-500">{facility.timezone}</span>
                    <span className="block text-xs text-slate-500">
                      {facility.propertyId === null
                        ? 'No Property ID reported to USPS'
                        : `Property ID ${facility.propertyId}`}
                    </span>
                  </td>
                  <td className="table-cell-base text-right tabular-nums">
                    {facility.workOrderCount}
                  </td>
                  <td className="table-cell-base text-right">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        clearError();
                        const open = editing === facility.id && panel === 'edit';
                        setPanel('edit');
                        setEditing(open ? null : facility.id);
                      }}
                    >
                      {editing === facility.id && panel === 'edit' ? 'Close' : 'Edit'}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        clearError();
                        const open = editing === facility.id && panel === 'reporting';
                        setPanel('reporting');
                        setEditing(open ? null : facility.id);
                      }}
                    >
                      {editing === facility.id && panel === 'reporting'
                        ? 'Close'
                        : 'USPS reporting'}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        clearError();
                        setActive.mutate({
                          facilityId: facility.id,
                          isActive: !facility.isActive,
                        });
                      }}
                    >
                      {facility.isActive ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null &&
        facilities
          .filter((facility) => facility.id === editing)
          .map((facility) =>
            panel === 'edit' ? (
              <FacilityEditFields
                key={facility.id}
                facility={facility}
                onSaved={() => {
                  setEditing(null);
                  invalidate();
                }}
                onCancel={() => setEditing(null)}
                onError={onError}
                clearError={clearError}
              />
            ) : (
              <FacilityReportingFields
                key={facility.id}
                facility={facility}
                onSaved={() => {
                  setEditing(null);
                  invalidate();
                }}
                onError={onError}
                clearError={clearError}
              />
            ),
          )}

      {adding && (
        <div className="mt-3 flex flex-col gap-3 rounded-md bg-slate-50 p-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Facility code"
              htmlFor={`code-${taskOrderId}`}
              required
              hint="2–10 uppercase letters or digits, no spaces or punctuation. Permanent."
              error={codeError}
            >
              <TextInput
                id={`code-${taskOrderId}`}
                value={form.facilityCode}
                onChange={(event) =>
                  setForm({ ...form, facilityCode: event.target.value.toUpperCase() })
                }
              />
            </Field>
            <Field label="Facility name" htmlFor={`name-${taskOrderId}`} required error={nameError}>
              <TextInput
                id={`name-${taskOrderId}`}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </Field>
            <Field label="City" htmlFor={`city-${taskOrderId}`}>
              <TextInput
                id={`city-${taskOrderId}`}
                value={form.city}
                onChange={(event) => setForm({ ...form, city: event.target.value })}
              />
            </Field>
            <Field label="State" htmlFor={`state-${taskOrderId}`} hint="Two-letter abbreviation.">
              <TextInput
                id={`state-${taskOrderId}`}
                maxLength={2}
                value={form.state}
                onChange={(event) => setForm({ ...form, state: event.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Time zone" htmlFor={`tz-${taskOrderId}`}>
              <Select
                id={`tz-${taskOrderId}`}
                value={form.timezone}
                onChange={(event) => setForm({ ...form, timezone: event.target.value })}
              >
                {TIME_ZONES.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={() => {
                clearError();
                create.mutate();
              }}
              disabled={!codeValid || !nameValid}
              loading={create.isPending}
            >
              Add facility
            </Button>
            {(!codeValid || !nameValid) && (
              <p className="text-xs text-slate-600">
                Enter a valid facility code and name to add the facility.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A task order with no COR of its own can name a USPS approver to stand in: they
 * alone receive requests within the COR limit until a COR is appointed.
 */
function ActingCorPicker({ taskOrder }: { taskOrder: AdminTaskOrder }) {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const users = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () =>
      apiRequest<{ users: AdminUser[] }>('/api/admin/users').then((result) => result.users),
  });
  const save = useMutation({
    mutationFn: (actingCorUserId: string | null) =>
      apiRequest(`/api/admin/task-orders/${taskOrder.id}`, {
        method: 'PATCH',
        body: { actingCorUserId },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'task-orders'] }),
  });
  if (!can(PERMISSIONS.ADMIN_TASK_ORDER_MANAGE)) return <>{taskOrder.actingCorName ?? '—'}</>;
  const approvers = (users.data ?? []).filter(
    (user) =>
      user.orgType === 'customer' &&
      user.isActive &&
      user.roleCodes.some((code) => code === 'customer_cor' || code === 'customer_co'),
  );
  return (
    <select
      className="rounded border border-slate-300 px-2 py-1 text-sm"
      value={taskOrder.actingCorUserId ?? ''}
      disabled={save.isPending}
      onChange={(event) => save.mutate(event.target.value || null)}
      aria-label={`Acting COR for ${taskOrder.number}`}
    >
      <option value="">None (use the task order's CORs)</option>
      {approvers.map((user) => (
        <option key={user.id} value={user.id}>
          {user.fullName}
        </option>
      ))}
    </select>
  );
}
